import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Upload, Search, Image, Check, X, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImagePickerModal - A reusable component for selecting images from the media gallery
 * 
 * Usage:
 * <ImagePickerModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSelect={(url) => handleImageSelected(url)}
 *   currentImage="/images/menu-uploads/current.png"
 * />
 */
const ImagePickerModal = ({ open, onClose, onSelect, currentImage }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUrl, setSelectedUrl] = useState(currentImage || '');

  const fetchImages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/uploads/images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchImages();
      setSelectedUrl(currentImage || '');
    }
  }, [open, currentImage]);

  const handleUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        // Auto-select the newly uploaded image
        setSelectedUrl(data.url);
        fetchImages();
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
    }
  };

  const filteredImages = images.filter(img =>
    img.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Choose Image from Gallery
          </DialogTitle>
          <DialogDescription>
            Upload a new image or select an existing one from your media gallery.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Upload & Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search images..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none text-sm"
              />
            </div>
            <div className="flex gap-2">
              <input
                type="file"
                id="modal-upload"
                accept="image/*"
                onChange={(e) => handleUpload(e.target.files[0])}
                className="hidden"
              />
              <label htmlFor="modal-upload">
                <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                  <span className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading ? 'Uploading...' : 'Upload New'}
                  </span>
                </Button>
              </label>
              <Button variant="ghost" size="sm" onClick={fetchImages}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Image Grid */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-8 h-8 animate-spin text-pink-500" />
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Image className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">
                  {searchTerm ? 'No images match your search' : 'No images in gallery'}
                </p>
                <label htmlFor="modal-upload" className="mt-3 inline-block">
                  <Button type="button" variant="outline" size="sm" asChild>
                    <span className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-1" />
                      Upload First Image
                    </span>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {filteredImages.map((image) => (
                  <button
                    key={image.filename}
                    type="button"
                    onClick={() => setSelectedUrl(image.url)}
                    className={`
                      relative aspect-square rounded-lg overflow-hidden border-2 transition-all
                      ${selectedUrl === image.url 
                        ? 'border-pink-500 ring-2 ring-pink-200' 
                        : 'border-gray-200 hover:border-pink-300'}
                    `}
                  >
                    <img
                      src={image.url}
                      alt={image.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10">Error</text></svg>';
                      }}
                    />
                    {selectedUrl === image.url && (
                      <div className="absolute inset-0 bg-pink-500/20 flex items-center justify-center">
                        <div className="bg-pink-500 text-white rounded-full p-1">
                          <Check className="w-4 h-4" />
                        </div>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                      <p className="text-white text-[10px] truncate">{image.filename}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Preview & Actions */}
          <div className="flex items-center justify-between gap-4 pt-4 border-t mt-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {selectedUrl ? (
                <>
                  <img
                    src={selectedUrl}
                    alt="Selected"
                    className="w-12 h-12 rounded-lg object-cover border"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Selected:</p>
                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{selectedUrl}</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">No image selected</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirm} 
                disabled={!selectedUrl}
                className="bg-pink-500 hover:bg-pink-600"
              >
                <Check className="w-4 h-4 mr-1" />
                Select Image
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImagePickerModal;
